/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import DisclaimerFooter from "components/DisclaimerFooter"
import { useEffect } from "react"
import { Link } from "react-router"
import { pageMetaData, updateMetaTags } from "utils/metaTags"

const Terms = () => {
  // Update meta tags for terms page
  useEffect(() => {
    const { title, description, keywords } = pageMetaData.terms
    updateMetaTags(title, description, keywords)
  }, [])
  return (
    <div className="pt-1 pb-3">
      <h1 className="fs-4 pb-3">Terms of Service</h1>

      {/* AIDEV-NOTE: #522 - this date is legally operative, not decoration. A disclaimer
          defence rests on showing what a given user saw on a given day. Update it whenever the
          wording below changes, and never as a routine touch. Guarded by
          utils/legalCopy.test.ts, which requires a parseable date on all three legal pages. */}
      <p>
        <strong>Last Updated:</strong> August 20, 2026
      </p>

      <h2 className="fs-5 mt-4 mb-3">Agreement to Terms</h2>
      {/* AIDEV-NOTE: #522 - the operator is named because "Safe Retirement Spending" is not a
          legal person, and the 2026-08-20 decision declined to form an LLC. A limitation of
          liability needs an identifiable party to run in favour of. This discloses nothing new:
          About.tsx already names the same person publicly.
          Deliberately NOT "trading as" or "d/b/a". Colorado requires a sole proprietor using any
          name other than their legal name to file a Statement of Trade Name, and that filing has
          not been made - so the term of art would assert a registered status the operator does
          not hold, on the page whose job is accuracy. That is the #935 defect class, introduced
          while fixing it. "who operates" states the fact and claims nothing. Do not reintroduce
          the term of art unless the trade name is actually registered. */}
      <p>
        These Terms of Service (&quot;Terms&quot;) constitute a legally binding agreement made
        between you and John Firth, who operates saferetirementspending.com (&quot;we,&quot;
        &quot;us,&quot; or &quot;our&quot;), concerning your access to and use of that website and
        our retirement planning calculator (collectively, the &quot;Service&quot;).
      </p>
      <p>
        You agree that by accessing the Service, you have read, understood, and agree to be bound by
        these Terms. If you do not agree with all of these Terms, then you are prohibited from using
        the Service and you should discontinue use immediately.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Intellectual Property Rights</h2>
      <p>
        The Service and its original content, features, and functionality are and will remain the
        exclusive property of Safe Retirement Spending and its licensors. The Service is protected
        by copyright, trademark, and other intellectual property laws. Our trademarks and trade
        dress may not be used in connection with any product or service without the prior written
        consent of Safe Retirement Spending.
      </p>

      <h2 className="fs-5 mt-4 mb-3">User Representations</h2>
      <p>By using the Service, you represent and warrant that:</p>
      <ul>
        <li>You are at least 18 years of age</li>
        <li>You have the legal capacity to agree to these Terms</li>
        <li>You will use the Service in accordance with these Terms</li>
        <li>You will not use the Service for any illegal or unauthorized purpose</li>
        <li>Your use of the Service will not violate any applicable law or regulation</li>
      </ul>

      <h2 className="fs-5 mt-4 mb-3">Prohibited Activities</h2>
      <p>
        You may not access or use the Service for any purpose other than that for which we make the
        Service available. The Service may not be used in connection with any commercial endeavors
        except those that are specifically endorsed or approved by us.
      </p>
      <p>As a user of the Service, you agree not to:</p>
      <ul>
        <li>
          Systematically retrieve data or other content from the Service to create or compile,
          directly or indirectly, a collection, compilation, database, or directory
        </li>
        <li>
          Circumvent, disable, or otherwise interfere with security-related features of the Service
        </li>
        <li>Engage in unauthorized framing of or linking to the Service</li>
        <li>Make improper use of our support services or submit false reports</li>
        <li>Use the Service in a manner inconsistent with any applicable laws or regulations</li>
      </ul>

      <h2 className="fs-5 mt-4 mb-3">User Data</h2>
      <p>
        You are solely responsible for all data that you transmit or that relates to any activity
        you have undertaken using the Service. You agree that we shall have no liability to you for
        any loss or corruption of any such data. As noted in our Privacy Policy, calculator inputs
        are not stored in a database after your session is complete; see our Privacy Policy for
        details on operational log retention.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Electronic Communications</h2>
      <p>
        Using the Service or sending us emails constitutes electronic communications. You consent to
        receive electronic communications, and you agree that all agreements, notices, disclosures,
        and other communications we provide to you electronically satisfy any legal requirement that
        such communications be in writing.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Modifications to Terms</h2>
      <p>
        We reserve the right, in our sole discretion, to make changes or modifications to these
        Terms at any time and for any reason. We will alert you about any changes by updating the
        &quot;Last Updated&quot; date of these Terms, and you waive any right to receive specific
        notice of each such change. It is your responsibility to periodically review these Terms to
        stay informed of updates.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Disclaimer of Warranties</h2>
      {/* AIDEV-NOTE: #522 item 2. A limitation of liability without a warranty disclaimer
          leaves the warranty claim itself open, and this page carried the former without the
          latter. Upper case matches the Limitation of Liability clause below and is the
          conventional signal of conspicuousness. Kept as a standard formula on purpose: the
          2026-08-20 decision declined legal review, so clauses that can be lifted accurately
          are written and clauses needing judgement (liability cap, indemnification,
          arbitration, class-action waiver) are deliberately absent - see the note after
          Severability. */}
      <p>
        THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS, WITHOUT
        WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE FULLEST
        EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING THE IMPLIED
        WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
        NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR
        ERROR-FREE, NOR THAT ANY FIGURE IT PRODUCES IS CORRECT, COMPLETE, OR SUITABLE FOR YOUR
        CIRCUMSTANCES.
      </p>
      <p>
        The Service is provided for educational purposes only and is not financial, investment, tax,
        or legal advice. Please read our <Link to="/disclaimers/">Disclaimers</Link>, which set out
        the limits of what the Service can tell you.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Limitation of Liability</h2>
      <p>
        IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD
        PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE
        DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM
        YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Governing Law</h2>
      {/* AIDEV-NOTE: #522 item 1. This clause used to name "the laws of the United States",
          which is not a body of contract law - contract and consumer law is state law - so it
          named nothing enforceable and gave no forum control. Colorado was chosen on
          2026-08-15. No county is named: venue by state alone is enforceable, and naming the
          operator's county of residence would disclose more than the 2026-08-15 decision
          against publishing a home address intended. */}
      <p>
        These Terms are governed by and construed in accordance with the laws of the State of
        Colorado, without regard to its conflict of law principles. Any dispute arising out of or
        relating to these Terms or the Service will be brought exclusively in the state or federal
        courts located in the State of Colorado, and you consent to the personal jurisdiction of
        those courts.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Severability</h2>
      <p>
        If any provision of these Terms is held to be invalid or unenforceable, that provision will
        be limited or removed to the minimum extent necessary, and the remaining provisions will
        continue in full force and effect.
      </p>
      {/* AIDEV-NOTE: #522 item 3 - four clauses are ABSENT BY DECISION, not by oversight:
          liability cap, indemnification, arbitration, and class-action waiver. The 2026-08-20
          decision declined legal review on cost grounds, and these are the clauses where
          self-drafting is risky rather than merely inadvisable - consumer arbitration and
          class-action provisions are heavily litigated, and a clause a court strikes invites an
          argument about the rest of the document. They are also contract theory, while the
          realistic claim here is tort (negligent misrepresentation), against which they do
          close to nothing. Do not add them without reopening #522. */}

      <h2 className="fs-5 mt-4 mb-3">Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at:{" "}
        <a href="mailto:support&#64;saferetirementspending&#46;com">
          support&#64;saferetirementspending&#46;com
        </a>
      </p>

      <p className="mt-4">
        Please also review our <Link to="/disclaimers/">Disclaimers</Link> and{" "}
        <Link to="/privacy/">Privacy Policy</Link>.
      </p>

      <DisclaimerFooter />
    </div>
  )
}

export default Terms
